/**
 * Admin Affiliate Management Page
 * For App.tsx admin system (port 5175)
 */

import { useState, useEffect } from 'react';
import { 
  Users as UserGroupIcon, 
  XCircle as XCircleIcon,
  Banknote as BanknotesIcon,
  Clock as ClockIcon,
  BarChart3 as ChartBarIcon,
  Search as SearchIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import { adminApi, useAdminStore } from '../stores/adminStore';

// Types
interface Application {
  id: string;
  user_id: string;
  user_email: string;
  reason: string;
  paypal_email: string;
  created_at: string;  // Changed from applied_at to match backend
  status: string;
}

interface Affiliate {
  id: string;
  email: string;
  affiliate_code: string;
  commission_rate: number | any; // Can be number or object with tier info
  status: string;
  total_earned: number;
  approved_balance: number;
  pending_balance: number;
  created_at: string;
  total_referrals?: number;
  total_conversions?: number;
  suspended_reason?: string;
  suspended_at?: string;
  suspended_by?: string | any; // Can be string (UUID) or object
}

interface Commission {
  id: string;
  affiliate_user_id: string;
  referral_id: string;
  referred_user_id: string;
  order_amount: number;
  commission_rate: number;
  commission_amount: number;
  commission_type: string;
  status: string;
  created_at: string;
  updated_at: string;
  paypal_order_id?: string;
  affiliate?: {
    id: string;
    name: string;
    affiliate_code: string;
  };
  referral?: {
    id: string;
    referred_user_id: string;
    referral_code: string;
  };
}

interface Payout {
  id: string;
  affiliate_user_id: string;
  amount: number;
  status: string;
  payment_email: string;
  payment_method: string;
  requested_at: string;
  created_at: string;
  completed_at?: string;
  transaction_id?: string;
  admin_notes?: string;
  failure_reason?: string;
  affiliate?: {
    id: string;
    name: string;
    affiliate_code: string;
  };
}

export default function AdminAffiliateManagementPage() {
  const [activeTab, setActiveTab] = useState<'applications' | 'affiliates' | 'payouts' | 'payout-history' | 'commissions'>('applications');
  const [loading, setLoading] = useState(false);
  
  // Get admin auth state for debugging
  const { token, admin, isAuthenticated } = useAdminStore();
  
  // Data states
  const [applications, setApplications] = useState<Application[]>([]);
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [payoutHistory, setPayoutHistory] = useState<Payout[]>([]);
  
  // Modal states
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Affiliate details modal states
  const [selectedAffiliate, setSelectedAffiliate] = useState<Affiliate | null>(null);
  const [showAffiliateDetailsModal, setShowAffiliateDetailsModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [customSuspendReason, setCustomSuspendReason] = useState('');
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);

  // Payout action modal states
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAction, setPayoutAction] = useState<'paid' | 'failed'>('paid');
  const [payoutTransactionId, setPayoutTransactionId] = useState('');
  const [payoutCompletedDate, setPayoutCompletedDate] = useState(new Date().toISOString().split('T')[0]);
  const [payoutAdminNotes, setPayoutAdminNotes] = useState('');
  const [payoutFailureReason, setPayoutFailureReason] = useState('');
  const [processingPayout, setProcessingPayout] = useState(false);

  // Filter and Pagination states for Applications
  const [applicationStatusFilter, setApplicationStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [searchQuery, setSearchQuery] = useState('');

  // Debug auth state on mount
  useEffect(() => {
    console.log('Admin Auth State:', {
      isAuthenticated,
      hasToken: !!token,
      tokenLength: token?.length,
      adminRole: admin?.role
    });
  }, []);

  // Fetch data on mount and tab change
  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'applications') {
        await fetchApplications();
      } else if (activeTab === 'affiliates') {
        await fetchAffiliates();
      } else if (activeTab === 'commissions') {
        await fetchCommissions();
      } else if (activeTab === 'payouts') {
        await fetchPayouts();
      } else if (activeTab === 'payout-history') {
        await fetchPayoutHistory();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    console.log('Fetching affiliate applications...');
    
    // Fetch ALL applications (pending, approved, rejected)
    const response = await adminApi.get('/admin/affiliates/applications?status=all');
    
    console.log('Applications response:', {
      status: response.status,
      data: response.data
    });
    
    const data = response.data;
    if (data.success) {
      setApplications(data.data?.applications || []);
    } else {
      console.error('Failed to fetch applications:', data);
    }
  };

  const fetchAffiliates = async () => {
  const response = await adminApi.get('/admin/affiliates/list');
    const data = response.data;
    if (data.success) {
      setAffiliates(data.data?.affiliates || []);
    }
  };

  const fetchCommissions = async () => {
  const response = await adminApi.get('/admin/affiliates/commissions/pending');
    const data = response.data;
    if (data.success) {
      setCommissions(data.data?.commissions || []);
    }
  };

  const fetchPayouts = async () => {
  const response = await adminApi.get('/admin/affiliates/payouts/pending');
    const data = response.data;
    if (data.success) {
      setPayouts(data.data?.payouts || []);
    }
  };

  const fetchPayoutHistory = async () => {
    const response = await adminApi.get('/admin/affiliates/payouts/all');
    const data = response.data;
    if (data.success) {
      setPayoutHistory(data.data?.payouts || []);
    }
  };

  // Actions
  const viewApplicationDetails = (app: Application) => {
    setSelectedApplication(app);
    setShowDetailsModal(true);
    setRejectReason('');
  };

  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedApplication(null);
    setRejectReason('');
  };

  const approveApplication = async (applicationId: string) => {
    try {
      const response = await adminApi.post(
        `/admin/affiliates/approve/${applicationId}`,
        { commission_rate: 20 }
      );
      
      const data = response.data;
      if (data.success) {
        toast.success('Application approved successfully!');
        closeModal();
        fetchApplications();
      } else {
        toast.error(data.error?.message || 'Failed to approve');
      }
    } catch (error: any) {
      console.error('Approve error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to approve application');
    }
  };

  const rejectApplication = async (applicationId: string) => {
    if (!rejectReason.trim()) {
      toast.error('Please enter a rejection reason');
      return;
    }

    try {
      const response = await adminApi.post(
        `/admin/affiliates/reject/${applicationId}`,
        { reason: rejectReason }
      );
      
      const data = response.data;
      if (data.success) {
        toast.success('Application rejected');
        closeModal();
        fetchApplications();
      } else {
        toast.error(data.error?.message || 'Failed to reject');
      }
    } catch (error: any) {
      console.error('Reject error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to reject application');
    }
  };

  const approveCommission = async (commissionId: string) => {
    if (!confirm('Approve this commission? This will add the amount to affiliate\'s approved balance.')) {
      return;
    }

    try {
      const response = await adminApi.post(`/admin/affiliates/commissions/${commissionId}/approve`);
      
      const data = response.data;
      if (data.success) {
        toast.success('✅ Commission approved! It will now appear in affiliate\'s Available for Payout balance and has been removed from pending list.', {
          duration: 5000,
          style: {
            background: '#10B981',
            color: '#fff',
          }
        });
        fetchCommissions();
      } else {
        toast.error(data.error?.message || 'Failed to approve commission');
      }
    } catch (error: any) {
      console.error('Approve commission error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to approve commission');
    }
  };

  const rejectCommission = async (commissionId: string) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason || !reason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      const response = await adminApi.post(
        `/admin/affiliates/commissions/${commissionId}/reject`,
        { reason: reason.trim() }
      );
      
      const data = response.data;
      if (data.success) {
        toast.success('❌ Commission rejected and removed from pending list.', {
          duration: 4000,
          style: {
            background: '#EF4444',
            color: '#fff',
          }
        });
        fetchCommissions();
      } else {
        toast.error(data.error?.message || 'Failed to reject commission');
      }
    } catch (error: any) {
      console.error('Reject commission error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to reject commission');
    }
  };

  const resetUserStatus = async (userId: string) => {
    if (!confirm('Allow this user to reapply? This will clear their rejection status.')) {
      return;
    }

    try {
      const response = await adminApi.post(`/admin/affiliates/reset-status/${userId}`);
      
      const data = response.data;
      if (data.success) {
        toast.success('User status reset. They can now reapply.');
        closeModal();
        fetchApplications();
      } else {
        toast.error(data.error?.message || 'Failed to reset status');
      }
    } catch (error: any) {
      console.error('Reset status error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to reset user status');
    }
  };

  const suspendAffiliate = async (affiliateId: string) => {
    // Use custom reason if 'other' is selected, otherwise use the selected reason
    const finalReason = suspendReason === 'other' ? customSuspendReason.trim() : suspendReason.trim();
    
    if (!finalReason) {
      toast.error('Please select or enter a suspension reason');
      return;
    }

    try {
      const response = await adminApi.post(
        `/admin/affiliates/suspend/${affiliateId}`,
        { 
          suspend: true,
          reason: finalReason 
        }
      );
      
      const data = response.data;
      if (data.success) {
        toast.success('Affiliate suspended successfully');
        setShowSuspendConfirm(false);
        setShowAffiliateDetailsModal(false);
        setSuspendReason('');
        setCustomSuspendReason('');
        fetchAffiliates();
      } else {
        toast.error(data.error?.message || 'Failed to suspend affiliate');
      }
    } catch (error: any) {
      console.error('Suspend affiliate error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to suspend affiliate');
    }
  };

  // Open payout action modal
  const openPayoutModal = (payout: Payout) => {
    setSelectedPayout(payout);
    setPayoutAction('paid');
    setPayoutTransactionId('');
    setPayoutCompletedDate(new Date().toISOString().split('T')[0]);
    setPayoutAdminNotes('');
    setPayoutFailureReason('');
    setShowPayoutModal(true);
  };

  // Close payout modal
  const closePayoutModal = () => {
    setShowPayoutModal(false);
    setSelectedPayout(null);
    setPayoutTransactionId('');
    setPayoutAdminNotes('');
    setPayoutFailureReason('');
    setProcessingPayout(false);
  };

  // Process payout (mark as paid/failed)
  const processPayout = async () => {
    if (!selectedPayout) return;

    // Validation
    if (payoutAction === 'paid') {
      if (!payoutTransactionId.trim()) {
        toast.error('PayPal Transaction ID is required');
        return;
      }
      if (!payoutCompletedDate) {
        toast.error('Completion date is required');
        return;
      }
    } else if (payoutAction === 'failed') {
      if (!payoutFailureReason.trim()) {
        toast.error('Failure reason is required');
        return;
      }
    }

    try {
      setProcessingPayout(true);

      const payload = {
        status: payoutAction === 'paid' ? 'completed' : 'failed',
        transaction_id: payoutAction === 'paid' ? payoutTransactionId.trim() : undefined,
        completed_at: payoutAction === 'paid' ? new Date(payoutCompletedDate).toISOString() : undefined,
        admin_notes: payoutAdminNotes.trim() || undefined,
        failure_reason: payoutAction === 'failed' ? payoutFailureReason.trim() : undefined
      };

      const response = await adminApi.post(`/admin/affiliates/payouts/${selectedPayout.id}/process`, payload);
      
      const data = response.data;
      if (data.success) {
        toast.success(payoutAction === 'paid' ? 'Payout marked as paid successfully' : 'Payout marked as failed');
        closePayoutModal();
        fetchPayouts();
      } else {
        toast.error(data.error?.message || 'Failed to process payout');
      }
    } catch (error: any) {
      console.error('Process payout error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to process payout');
    } finally {
      setProcessingPayout(false);
    }
  };

  const unsuspendAffiliate = async (affiliateId: string) => {
    if (!confirm('Are you sure you want to unsuspend this affiliate?')) {
      return;
    }

    try {
      const response = await adminApi.post(
        `/admin/affiliates/unsuspend/${affiliateId}`,
        { suspend: false }
      );
      
      const data = response.data;
      if (data.success) {
        toast.success('Affiliate unsuspended successfully');
        setShowAffiliateDetailsModal(false);
        fetchAffiliates();
      } else {
        toast.error(data.error?.message || 'Failed to unsuspend affiliate');
      }
    } catch (error: any) {
      console.error('Unsuspend affiliate error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to unsuspend affiliate');
    }
  };

  // Helper function to calculate duration
  const calculateDuration = (joinDate: string): string => {
    const join = new Date(joinDate);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - join.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `${diffDays} days`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const remainingMonths = Math.floor((diffDays % 365) / 30);
      return `${years} year${years > 1 ? 's' : ''}${remainingMonths > 0 ? ` ${remainingMonths}mo` : ''}`;
    }
  };

  // Filter and sort applications
  const getFilteredAndSortedApplications = () => {
    let filtered = applications;

    // Apply status filter
    if (applicationStatusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === applicationStatusFilter);
    }

    // Apply search filter (search in email or paypal_email)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(app => 
        app.user_email?.toLowerCase().includes(query) ||
        app.paypal_email?.toLowerCase().includes(query)
      );
    }

    // Sort: pending first, then by created_at descending
    const sorted = [...filtered].sort((a, b) => {
      // Priority: pending > approved > rejected
      const statusPriority = { pending: 1, approved: 2, rejected: 3 };
      const priorityA = statusPriority[a.status as keyof typeof statusPriority] || 4;
      const priorityB = statusPriority[b.status as keyof typeof statusPriority] || 4;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // Within same status, newest first
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return sorted;
  };

  // Pagination
  const getPaginatedApplications = () => {
    const sorted = getFilteredAndSortedApplications();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sorted.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(getFilteredAndSortedApplications().length / itemsPerPage);
  const paginatedApplications = getPaginatedApplications();

  // Reset page when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [applicationStatusFilter, searchQuery]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Affiliate Management</h2>
        <p className="text-gray-600">Manage affiliate applications, commissions, and payouts</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('applications')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'applications'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <UserGroupIcon className="h-5 w-5" />
              Applications
            </div>
          </button>

          <button
            onClick={() => setActiveTab('affiliates')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'affiliates'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5" />
              All Affiliates
            </div>
          </button>

          <button
            onClick={() => setActiveTab('commissions')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'commissions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <ClockIcon className="h-5 w-5" />
              Pending Commissions
            </div>
          </button>

          <button
            onClick={() => setActiveTab('payouts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'payouts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <BanknotesIcon className="h-5 w-5" />
              Pending Payouts
            </div>
          </button>

          <button
            onClick={() => setActiveTab('payout-history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'payout-history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5" />
              Payout History
            </div>
          </button>
        </nav>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div className="bg-white rounded-lg shadow">
              {/* Filter and Search Bar */}
              <div className="px-6 py-4 border-b border-gray-200 space-y-4">
                {/* Status Filter Buttons */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Filter:</span>
                  <button
                    onClick={() => setApplicationStatusFilter('pending')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      applicationStatusFilter === 'pending'
                        ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Pending ({applications.filter(a => a.status === 'pending').length})
                  </button>
                  <button
                    onClick={() => setApplicationStatusFilter('approved')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      applicationStatusFilter === 'approved'
                        ? 'bg-green-100 text-green-800 border-2 border-green-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Approved ({applications.filter(a => a.status === 'approved').length})
                  </button>
                  <button
                    onClick={() => setApplicationStatusFilter('rejected')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      applicationStatusFilter === 'rejected'
                        ? 'bg-red-100 text-red-800 border-2 border-red-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Rejected ({applications.filter(a => a.status === 'rejected').length})
                  </button>
                  <button
                    onClick={() => setApplicationStatusFilter('all')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      applicationStatusFilter === 'all'
                        ? 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All ({applications.length})
                  </button>
                </div>

                {/* Search Input */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SearchIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by email or PayPal email..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <XCircleIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
              </div>

              {paginatedApplications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {searchQuery ? (
                    <div>
                      <p className="text-gray-600 font-medium">No applications found matching "{searchQuery}"</p>
                      <p className="text-sm mt-2">Try a different email or clear the search</p>
                    </div>
                  ) : (
                    <p>No {applicationStatusFilter !== 'all' ? applicationStatusFilter : ''} applications</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PayPal</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {paginatedApplications.map((app) => (
                          <tr key={app.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{app.user_email}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{app.paypal_email || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{new Date(app.created_at).toLocaleDateString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                app.status === 'approved' ? 'bg-green-100 text-green-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {app.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                              <button
                                onClick={() => viewApplicationDetails(app)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, getFilteredAndSortedApplications().length)} of {getFilteredAndSortedApplications().length} applications
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        
                        <div className="flex items-center gap-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-1 rounded-md text-sm font-medium ${
                                currentPage === page
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-700 hover:bg-gray-50 border border-gray-300'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                        </div>

                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Affiliates Tab */}
          {activeTab === 'affiliates' && (
            <div className="bg-white rounded-lg shadow">
              {affiliates.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No affiliates found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Earned</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {affiliates.map((aff) => (
                        <tr key={aff.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{aff.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">{aff.affiliate_code}</code>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {aff.created_at ? new Date(aff.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {aff.created_at ? calculateDuration(aff.created_at) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                            ${aff.total_earned?.toFixed(2) || '0.00'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {aff.status === 'suspended' ? (
                              <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800 font-medium">
                                🚫 Suspended
                              </span>
                            ) : aff.status === 'active' ? (
                              <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 font-medium">
                                ✓ Active
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                                {aff.status}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => {
                                setSelectedAffiliate(aff);
                                setShowAffiliateDetailsModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Commissions Tab */}
          {activeTab === 'commissions' && (
            <div className="bg-white rounded-lg shadow">
              {commissions.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No pending commissions
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Affiliate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PayPal Order ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {commissions.map((comm) => (
                        <tr key={comm.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">{comm.id.substring(0, 8)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {comm.affiliate?.name || comm.affiliate_user_id.substring(0, 8)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {comm.paypal_order_id ? (
                              <a 
                                href={`https://www.paypal.com/activity/payment/${comm.paypal_order_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline font-mono text-xs"
                                title="Check in PayPal"
                              >
                                {comm.paypal_order_id.substring(0, 16)}...
                              </a>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">${comm.order_amount.toFixed(2)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                            ${comm.commission_amount.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{comm.commission_rate}%</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              comm.commission_type === 'first_payment' ? 'bg-blue-100 text-blue-800' :
                              comm.commission_type === 'recurring' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {comm.commission_type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              comm.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              comm.status === 'approved' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {comm.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{new Date(comm.created_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                            {comm.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => approveCommission(comm.id)}
                                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs"
                                  title="Verify payment in PayPal first!"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => rejectCommission(comm.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {comm.status !== 'pending' && (
                              <span className="text-gray-400 text-xs">No actions</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Payouts Tab */}
          {activeTab === 'payouts' && (
            <div className="bg-white rounded-lg shadow">
              {payouts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No pending payouts
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payout ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Affiliate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {payouts.map((payout) => (
                        <tr key={payout.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">{payout.id.substring(0, 8)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {payout.affiliate?.name || payout.affiliate_user_id.substring(0, 8)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{payout.payment_email || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                              {payout.payment_method}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">${payout.amount.toFixed(2)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              payout.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              payout.status === 'completed' ? 'bg-green-100 text-green-800' :
                              payout.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {payout.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{new Date(payout.requested_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {payout.status === 'pending' ? (
                              <button
                                onClick={() => openPayoutModal(payout)}
                                className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-xs font-medium"
                              >
                                Process
                              </button>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Payout History Tab */}
          {activeTab === 'payout-history' && (
            <div className="bg-white rounded-lg shadow">
              {payoutHistory.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No payout history yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payout ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Affiliate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {payoutHistory.map((payout) => (
                        <tr key={payout.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">{payout.id.substring(0, 8)}...</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {payout.affiliate?.name || payout.affiliate_user_id.substring(0, 8)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{payout.payment_email || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">${payout.amount.toFixed(2)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              payout.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              payout.status === 'completed' ? 'bg-green-100 text-green-800' :
                              payout.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                              payout.status === 'failed' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {payout.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{new Date(payout.requested_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {payout.completed_at ? new Date(payout.completed_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                            {payout.transaction_id || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm max-w-xs">
                            {payout.status === 'failed' && payout.failure_reason ? (
                              <div className="space-y-1">
                                <div className="text-red-600 font-medium">❌ Failure: {payout.failure_reason}</div>
                                {payout.admin_notes && <div className="text-gray-600">📝 {payout.admin_notes}</div>}
                              </div>
                            ) : payout.admin_notes ? (
                              <div className="text-gray-600">📝 {payout.admin_notes}</div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Application Details Modal */}
      {showDetailsModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Application Details</h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              {/* User Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
                <p className="text-gray-900">{selectedApplication.user_email}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PayPal Email</label>
                <p className="text-gray-900">{selectedApplication.paypal_email || 'Not provided'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Application Date</label>
                <p className="text-gray-900">{new Date(selectedApplication.created_at).toLocaleString()}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <span className={`inline-block px-3 py-1 rounded-full text-sm ${
                  selectedApplication.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  selectedApplication.status === 'approved' ? 'bg-green-100 text-green-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {selectedApplication.status}
                </span>
              </div>

              {/* Full Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Why do they want to become an affiliate?
                </label>
                <div className="bg-gray-50 p-4 rounded border border-gray-200">
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedApplication.reason}</p>
                </div>
              </div>

              {/* Reject Reason Input (only show when rejecting) */}
              {selectedApplication.status === 'pending' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rejection Reason (if rejecting)
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Enter reason for rejection..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            {/* Footer Actions */}
            {selectedApplication.status === 'pending' && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => rejectApplication(selectedApplication.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Reject Application
                </button>
                <button
                  onClick={() => approveApplication(selectedApplication.id)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Approve Application
                </button>
              </div>
            )}

            {/* Already processed - show different actions based on status */}
            {selectedApplication.status !== 'pending' && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                {/* Show "Allow Reapply" button only for rejected applications */}
                {selectedApplication.status === 'rejected' && (
                  <button
                    onClick={() => resetUserStatus(selectedApplication.user_id)}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                  >
                    Allow Reapply
                  </button>
                )}
                <button
                  onClick={closeModal}
                  className={`px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 ${
                    selectedApplication.status === 'rejected' ? '' : 'ml-auto'
                  }`}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Affiliate Details Modal */}
      {showAffiliateDetailsModal && selectedAffiliate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Affiliate Details</h3>
                <p className="text-sm text-gray-500 mt-1">{selectedAffiliate.email}</p>
              </div>
              <button
                onClick={() => {
                  setShowAffiliateDetailsModal(false);
                  setShowSuspendConfirm(false);
                  setSuspendReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-6">
              {/* Status Banner */}
              {selectedAffiliate.status === 'suspended' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-red-800">Account Suspended</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p><strong>Reason:</strong> {selectedAffiliate.suspended_reason || 'No reason provided'}</p>
                        {selectedAffiliate.suspended_at && (
                          <p className="mt-1 text-xs"><strong>Suspended on:</strong> {new Date(selectedAffiliate.suspended_at).toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                  <div className="text-xs font-medium text-blue-600 uppercase">Total Earned</div>
                  <div className="text-2xl font-bold text-blue-900 mt-1">
                    ${selectedAffiliate.total_earned?.toFixed(2) || '0.00'}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                  <div className="text-xs font-medium text-green-600 uppercase">Available</div>
                  <div className="text-2xl font-bold text-green-900 mt-1">
                    ${selectedAffiliate.approved_balance?.toFixed(2) || '0.00'}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-lg border border-yellow-200">
                  <div className="text-xs font-medium text-yellow-600 uppercase">Pending</div>
                  <div className="text-2xl font-bold text-yellow-900 mt-1">
                    ${selectedAffiliate.pending_balance?.toFixed(2) || '0.00'}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                  <div className="text-xs font-medium text-purple-600 uppercase">Commission</div>
                  <div className="text-2xl font-bold text-purple-900 mt-1">
                    {typeof selectedAffiliate.commission_rate === 'number' 
                      ? `${selectedAffiliate.commission_rate}%` 
                      : typeof selectedAffiliate.commission_rate === 'object' && selectedAffiliate.commission_rate?.tier
                        ? `${selectedAffiliate.commission_rate.tier}%`
                        : '20%'
                    }
                  </div>
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Affiliate Code</label>
                  <div className="flex items-center space-x-2">
                    <code className="px-3 py-2 bg-gray-100 rounded font-mono text-sm">{selectedAffiliate.affiliate_code}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(selectedAffiliate.affiliate_code);
                        toast.success('Code copied!');
                      }}
                      className="text-blue-600 hover:text-blue-800 text-xs"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <div>
                    {selectedAffiliate.status === 'suspended' ? (
                      <span className="px-3 py-1 rounded-full text-sm bg-red-100 text-red-800 font-medium">
                        🚫 Suspended
                      </span>
                    ) : selectedAffiliate.status === 'active' ? (
                      <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 font-medium">
                        ✓ Active
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                        {selectedAffiliate.status}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Join Date</label>
                  <p className="text-gray-900">
                    {selectedAffiliate.created_at ? new Date(selectedAffiliate.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : '-'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                  <p className="text-gray-900">
                    {selectedAffiliate.created_at ? calculateDuration(selectedAffiliate.created_at) : '-'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Referrals</label>
                  <p className="text-gray-900">{selectedAffiliate.total_referrals || 0} clicks</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Conversions</label>
                  <p className="text-gray-900">{selectedAffiliate.total_conversions || 0} sales</p>
                </div>
              </div>

              {/* Performance Metrics */}
              {selectedAffiliate.total_referrals && selectedAffiliate.total_referrals > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Performance Metrics</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs text-gray-600">Conversion Rate</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {((selectedAffiliate.total_conversions || 0) / selectedAffiliate.total_referrals * 100).toFixed(1)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Avg. per Sale</div>
                      <div className="text-lg font-semibold text-gray-900">
                        ${selectedAffiliate.total_conversions && selectedAffiliate.total_conversions > 0 
                          ? (selectedAffiliate.total_earned / selectedAffiliate.total_conversions).toFixed(2) 
                          : '0.00'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Earnings per Click</div>
                      <div className="text-lg font-semibold text-gray-900">
                        ${(selectedAffiliate.total_earned / selectedAffiliate.total_referrals).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Suspend Section */}
              {selectedAffiliate.status !== 'suspended' && !showSuspendConfirm && (
                <div className="border-t pt-4">
                  <button
                    onClick={() => setShowSuspendConfirm(true)}
                    className="text-red-600 hover:text-red-800 font-medium text-sm"
                  >
                    🚫 Suspend this affiliate
                  </button>
                </div>
              )}

              {/* Suspend Confirmation */}
              {showSuspendConfirm && selectedAffiliate.status !== 'suspended' && (
                <div className="border-t pt-4 space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-yellow-800 mb-2">⚠️ Suspend Affiliate</h4>
                    <p className="text-sm text-yellow-700">
                      Suspending this affiliate will immediately stop all tracking and prevent them from earning commissions.
                      Their referral link will no longer work.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Suspension Reason <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={suspendReason}
                      onChange={(e) => setSuspendReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 mb-2"
                    >
                      <option value="">Select a reason...</option>
                      <option value="fraud">🚨 Fraudulent Activity</option>
                      <option value="fake_traffic">🤖 Fake/Bot Traffic</option>
                      <option value="spam">📧 Spamming</option>
                      <option value="tos_violation">📋 Terms of Service Violation</option>
                      <option value="unethical_promotion">⚠️ Unethical Promotion Methods</option>
                      <option value="chargeback">💳 Multiple Chargebacks</option>
                      <option value="trademark_abuse">™️ Trademark/Brand Abuse</option>
                      <option value="other">❓ Other (specify below)</option>
                    </select>
                    
                    {suspendReason === 'other' && (
                      <textarea
                        value={customSuspendReason}
                        onChange={(e) => setCustomSuspendReason(e.target.value)}
                        placeholder="Please specify the reason for suspension..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    )}
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => {
                        setShowSuspendConfirm(false);
                        setSuspendReason('');
                        setCustomSuspendReason('');
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => suspendAffiliate(selectedAffiliate.id)}
                      disabled={!suspendReason || (suspendReason === 'other' && !customSuspendReason.trim())}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Confirm Suspension
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              {selectedAffiliate.status === 'suspended' ? (
                <button
                  onClick={() => unsuspendAffiliate(selectedAffiliate.id)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  ✓ Unsuspend Affiliate
                </button>
              ) : (
                <div></div>
              )}
              <button
                onClick={() => {
                  setShowAffiliateDetailsModal(false);
                  setShowSuspendConfirm(false);
                  setSuspendReason('');
                  setCustomSuspendReason('');
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payout Process Modal */}
      {showPayoutModal && selectedPayout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Process Payout Request</h3>
              <button
                onClick={closePayoutModal}
                className="text-gray-400 hover:text-gray-600"
                disabled={processingPayout}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              {/* Payout Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Payout ID:</span>
                  <span className="text-sm font-mono">{selectedPayout.id.substring(0, 8)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Affiliate:</span>
                  <span className="text-sm font-medium">{selectedPayout.affiliate?.name || 'Unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Payment Email:</span>
                  <span className="text-sm font-medium">{selectedPayout.payment_email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Amount:</span>
                  <span className="text-lg font-bold text-green-600">${selectedPayout.amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Requested:</span>
                  <span className="text-sm">{new Date(selectedPayout.requested_at).toLocaleString()}</span>
                </div>
              </div>

              {/* Action Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Action <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-4">
                  <button
                    onClick={() => setPayoutAction('paid')}
                    className={`flex-1 px-4 py-2 rounded-md font-medium ${
                      payoutAction === 'paid'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    disabled={processingPayout}
                  >
                    ✓ Mark as Paid
                  </button>
                  <button
                    onClick={() => setPayoutAction('failed')}
                    className={`flex-1 px-4 py-2 rounded-md font-medium ${
                      payoutAction === 'failed'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    disabled={processingPayout}
                  >
                    ✗ Mark as Failed
                  </button>
                </div>
              </div>

              {/* Conditional Fields based on action */}
              {payoutAction === 'paid' ? (
                <>
                  {/* Transaction ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PayPal Transaction ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={payoutTransactionId}
                      onChange={(e) => setPayoutTransactionId(e.target.value)}
                      placeholder="e.g., 1234567890ABCDEF"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      disabled={processingPayout}
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter the PayPal transaction/transfer ID</p>
                  </div>

                  {/* Completion Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Completion Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={payoutCompletedDate}
                      onChange={(e) => setPayoutCompletedDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      disabled={processingPayout}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Failure Reason */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Failure Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={payoutFailureReason}
                      onChange={(e) => setPayoutFailureReason(e.target.value)}
                      placeholder="e.g., Invalid PayPal email, Account restricted, etc."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      disabled={processingPayout}
                    />
                  </div>
                </>
              )}

              {/* Admin Notes (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin Notes (Optional)
                </label>
                <textarea
                  value={payoutAdminNotes}
                  onChange={(e) => setPayoutAdminNotes(e.target.value)}
                  placeholder="Any additional notes..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={processingPayout}
                />
              </div>

              {/* Warning */}
              <div className={`rounded-lg p-4 ${
                payoutAction === 'paid' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-sm ${payoutAction === 'paid' ? 'text-green-700' : 'text-red-700'}`}>
                  {payoutAction === 'paid' 
                    ? '✓ This will mark the payout as completed and update all related commissions to "paid" status.'
                    : '⚠️ This will mark the payout as failed. The affiliate will need to update their payment details and request again.'
                  }
                </p>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <button
                onClick={closePayoutModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                disabled={processingPayout}
              >
                Cancel
              </button>
              <button
                onClick={processPayout}
                disabled={processingPayout}
                className={`px-6 py-2 rounded-md text-white font-medium ${
                  payoutAction === 'paid'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {processingPayout ? 'Processing...' : payoutAction === 'paid' ? 'Confirm Payment' : 'Mark as Failed'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
